import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Search, Clock, User, AlertCircle, RefreshCw, Building, Calendar, Eye, X } from 'lucide-react';
import { TicketFavorito } from '@/types';
import useFavoritos from '@/hooks/useFavoritos';
import FavoriteButton from '@/components/FavoriteButton';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ConfirmationModal from '@/components/ConfirmationModal';

interface Pagination {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

const Favoritos: React.FC = () => {
  const { favoritos, loading, error, atualizarFavoritos } = useFavoritos();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFavoritos, setFilteredFavoritos] = useState<TicketFavorito[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [ticketToRemove, setTicketToRemove] = useState<number | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Filtrar favoritos baseado no termo de busca
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredFavoritos(favoritos);
    } else {
      const filtered = favoritos.filter(favorito =>
        favorito.ticket_assunto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        favorito.ticket_id.toString().includes(searchTerm) ||
        favorito.ticket_status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        favorito.ticket_prioridade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        favorito.ticket_criador_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        favorito.ticket_departamento_nome?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredFavoritos(filtered);
    }
  }, [favoritos, searchTerm]);

  // Update pagination when filtered results change
  useEffect(() => {
    const totalItems = filteredFavoritos.length;
    const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);
    
    setPagination(prev => ({
      ...prev,
      totalItems,
      totalPages,
      currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
    }));
  }, [filteredFavoritos, pagination.itemsPerPage]);

  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return filteredFavoritos.slice(startIndex, endIndex);
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleRefresh = () => {
    atualizarFavoritos();
  };

  const handleRemoveFavorite = (ticketId: number) => {
    setTicketToRemove(ticketId);
    setShowConfirmModal(true);
  };

  const confirmRemoveFavorite = async () => {
    if (!ticketToRemove) return;

    setIsRemoving(true);
    try {
      const response = await fetch(`/api/favoritos`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ ticket_id: ticketToRemove })
      });

      if (response.ok) {
        // Refresh the favorites list
        atualizarFavoritos();
        setShowConfirmModal(false);
        setTicketToRemove(null);
      } else {
        console.error('Erro ao remover favorito');
        alert('Erro ao remover ticket dos favoritos');
      }
    } catch (error) {
      console.error('Erro ao remover favorito:', error);
      alert('Erro ao remover ticket dos favoritos');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCloseModal = () => {
    if (isRemoving) return;
    setShowConfirmModal(false);
    setTicketToRemove(null);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Data inválida';
    }
  };

  const getStatusBadgeColor = (status?: string) => {
    switch (status) {
      case 'Aberto': return 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700';
      case 'Em Andamento': return 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-700';
      case 'Fechado': return 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700';
      case 'Pendente': return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-700';
      case 'Resolvido': return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600';
    }
  };

  const getPriorityBadgeColor = (priority?: string) => {
    switch (priority) {
      case 'Alta': return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700';
      case 'Média': return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700';
      case 'Baixa': return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700';
      case 'Crítica': return 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-700';
      case 'Urgente': return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-700';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
        <div className="max-w-7xl mx-auto">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-lg">
                  <Heart className="w-6 h-6 text-red-600 dark:text-red-400 fill-current" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                  Tickets Favoritos
                </h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Gerencie e acesse rapidamente seus tickets favoritos
              </p>
            </div>
            
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-primary-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50 shadow-soft mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Buscar por assunto, ID, status, prioridade, criador ou departamento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Erro ao carregar favoritos</span>
            </div>
            <p className="text-red-600 dark:text-red-400 mt-1">{error}</p>
          </div>
        )}

        {/* Favorites Table */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Assunto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Criado por
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Departamento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Prioridade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {getCurrentPageItems().length > 0 ? (
                  getCurrentPageItems().map((favorito) => (
                    <tr key={favorito.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                          <Link
                            to={`/tickets/${favorito.ticket_id}`}
                            className="hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                          >
                            #{favorito.ticket_id}
                          </Link>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-gray-100 max-w-xs">
                          <span className="break-words">
                            {favorito.ticket_assunto || 'Assunto não especificado'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {favorito.ticket_criador_nome || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {favorito.ticket_departamento_nome || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {favorito.ticket_status && (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(favorito.ticket_status)}`}>
                            {favorito.ticket_status}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {favorito.ticket_prioridade && (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadgeColor(favorito.ticket_prioridade)}`}>
                            {favorito.ticket_prioridade}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleRemoveFavorite(favorito.ticket_id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded transition-colors"
                            title="Remover dos favoritos"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      <Heart className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                      <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        {searchTerm ? 'Nenhum favorito encontrado' : 'Nenhum ticket favorito'}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {searchTerm
                          ? 'Não há tickets favoritos que correspondam à sua busca.'
                          : 'Você ainda não marcou nenhum ticket como favorito.'}
                      </p>
                      {!searchTerm && (
                        <Link
                          to="/"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-primary-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-primary-600 transition-colors mt-4"
                        >
                          Ver Todos os Tickets
                        </Link>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
                Mostrando {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} a {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} de {pagination.totalItems} resultados
              </div>
              <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-2">
                {/* Botão Anterior */}
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${
                    pagination.currentPage === 1
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  ← Anterior
                </button>

                {/* Números das páginas */}
                <div className="flex items-center justify-center space-x-1 flex-wrap">
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => {
                    // Mostrar apenas algumas páginas para não sobrecarregar a interface
                    if (
                      page === 1 ||
                      page === pagination.totalPages ||
                      (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border min-w-[2.5rem] sm:min-w-[2.75rem] ${
                            page === pagination.currentPage
                              ? 'bg-blue-600 dark:bg-primary-500 text-white border-blue-600 dark:border-primary-500'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      page === pagination.currentPage - 2 ||
                      page === pagination.currentPage + 2
                    ) {
                      return <span key={page} className="px-1 sm:px-2 text-gray-400 text-xs">...</span>;
                    }
                    return null;
                  })}
                </div>

                {/* Botão Próximo */}
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${
                    pagination.currentPage === pagination.totalPages
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  Próximo →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        {filteredFavoritos.length > 0 && (
          <div className="mt-5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
              <div>
                Mostrando {filteredFavoritos.length} de {favoritos.length} ticket(s) favorito(s)
              </div>
              {searchTerm && (
                <div className="text-blue-600 dark:text-blue-400">
                  Filtrado por: "{searchTerm}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCloseModal}
        onConfirm={confirmRemoveFavorite}
        title="Remover dos Favoritos"
        message="Tem certeza que deseja remover este ticket dos favoritos?"
        confirmText="Remover"
        cancelText="Cancelar"
        type="danger"
        isLoading={isRemoving}
      />
    </div>
  );
};

export default Favoritos;